import { useMemo } from 'react';

import { Separator, Skeleton } from 'design-system';

import { useUserDateFormat } from '@/features/_shared/user';
import { useFactsheetMapImage } from '@/features/factsheet/api/queries';
import {
  formatFactsheetDate,
  formatFactsheetDateWithFallback
} from '@/features/factsheet/lib/dateFormatting';
import { useIsFactsheetCompact } from '@/features/factsheet/state/useIsFactsheetCompact';
import type { FactsheetDetails } from '@/features/factsheet/types';
import { cn } from '@/lib/utils';

/**
 * Inserts zero-width spaces after common break characters to allow
 * long text to wrap gracefully without breaking layout.
 */
const insertSoftBreaks = (text: string): string => text.replaceAll(/([ _-])/g, '$1​');

interface MapImageProps {
  location: string;
  zoom: string;
}

const MapImage = ({ location, zoom }: MapImageProps) => {
  const { data: imageSrc, isError } = useFactsheetMapImage(location, zoom);
  const isCompact = useIsFactsheetCompact();

  if (isError) {
    return null;
  }

  return (
    <div className={isCompact ? 'w-full' : 'w-2/5'}>
      {imageSrc ? (
        <img
          alt='factsheetImage'
          src={imageSrc}
          className='w-full'
          style={{ aspectRatio: 1 }}
        />
      ) : (
        <Skeleton
          className='w-full'
          style={{ aspectRatio: 1, height: 'auto' }}
        />
      )}
    </div>
  );
};

interface TileProps {
  label: string;
  value: string | boolean | string[] | undefined;
}

const Tile = ({ label, value }: TileProps) => {
  const isCompact = useIsFactsheetCompact();

  if (!value) return null;

  const values = Array.isArray(value) ? value : [value.toString()];

  return (
    <div
      className={cn(
        'flex flex-col items-start justify-center overflow-hidden break-words',
        isCompact ? 'min-w-0 max-w-[180px] p-0' : 'max-w-[180px] p-3'
      )}
    >
      <p className={cn('font-bold', isCompact && 'text-sm')}>{label}</p>
      {values.map((item, index) => (
        <p
          key={`${item}-${index}`}
          className={cn('break-words whitespace-pre-wrap', isCompact && 'text-sm')}
        >
          {insertSoftBreaks(item)}
        </p>
      ))}
    </div>
  );
};

interface DetailsSectionProps {
  children: React.ReactNode;
}

const DetailsSection = ({ children }: DetailsSectionProps) => {
  const isCompact = useIsFactsheetCompact();

  return (
    <div
      className={cn(
        'grid items-start gap-4',
        isCompact ? 'grid-cols-[repeat(auto-fill,minmax(120px,1fr))]' : 'grid-cols-5'
      )}
    >
      {children}
    </div>
  );
};

interface DetailsTabProps {
  factsheet: FactsheetDetails;
  isAuthenticated: boolean;
}

const DetailsTab = ({ factsheet, isAuthenticated }: DetailsTabProps) => {
  const dateFormat = useUserDateFormat();
  const { indexDetails, symbols, mapLocation, mapZoom } = factsheet;
  const isCompact = useIsFactsheetCompact();

  const startDate = useMemo(
    () => formatFactsheetDate(indexDetails['startDate'] as string, dateFormat.value),
    [dateFormat.value, indexDetails]
  );

  const endDate = useMemo(
    () =>
      formatFactsheetDateWithFallback(
        indexDetails['endDate'] as string | undefined,
        dateFormat.value,
        '(ongoing)'
      ),
    [dateFormat.value, indexDetails]
  );

  const primaryTiles: TileProps[] = [
    { label: 'Currency / Units', value: `${indexDetails['currency']} / ${indexDetails['units']}` },
    { label: 'Frequency', value: indexDetails['frequency'] },
    { label: 'Pricing Basis', value: indexDetails['pricingBasis'] },
    { label: 'Timezone', value: indexDetails['timeZone'] },
    { label: 'Trading Hub', value: indexDetails['tradingHub'] },
    { label: 'Delivery Basis', value: indexDetails['deliveryBasis'] },
    { label: 'Commodity', value: indexDetails['commodity'] },
    { label: 'Index Module(s)', value: indexDetails['indexModules'] },
    { label: 'Holiday Calendar', value: indexDetails['holidayCalendar'] },
    { label: 'Last Trade Calendar', value: indexDetails['lastTradeCalendar'] },
    { label: 'Columns Available', value: indexDetails['columnsAvailable'] },
    { label: 'Increment', value: indexDetails['increment'] }
  ];

  return (
    <div className={cn('flex', isCompact ? 'flex-col gap-2' : 'flex-row gap-6')}>
      <div
        className={cn(
          'flex flex-1 flex-col rounded-xl border border-border bg-card p-4',
          isCompact ? 'gap-4' : 'gap-0'
        )}
      >
        <DetailsSection>
          {primaryTiles.map(tile => (
            <Tile
              key={tile.label}
              {...tile}
            />
          ))}
        </DetailsSection>

        <Separator className='bg-gx-tile-light' />

        <DetailsSection>
          <Tile
            label='Status'
            value={indexDetails['status']}
          />
          <Tile
            label='Start Date'
            value={startDate}
          />
          <Tile
            label='End Date'
            value={endDate}
          />
        </DetailsSection>

        <Separator className='bg-gx-tile-light' />

        <DetailsSection>
          <Tile
            label='Spot Alias'
            value={indexDetails['spotAlias'] || '(none)'}
          />
          <Tile
            label='Forward Curve'
            value={indexDetails['forwardCurve'] ? 'Yes' : 'No'}
          />
          <Tile
            label='Num Items'
            value={symbols?.length.toString()}
          />
        </DetailsSection>
      </div>

      {isAuthenticated && mapLocation && (
        <MapImage
          location={mapLocation}
          zoom={mapZoom ?? '10'}
        />
      )}
    </div>
  );
};

export default DetailsTab;
